<template>
  <div v-show="visible" class="tab-pane-content">
    <!-- Table -->
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleGridEditClick"
      @button-click="handleGridButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface TabProps {
  visible?: boolean;
}

const props = withDefaults(defineProps<TabProps>(), {
  visible: false
});

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();

const dataTableRef = ref<DataTableInstance | null>(null);

// Endpoint
const endpoint = computed(() => '/index.php/gestao_clientes/carrega_compromissos_cancelados');

// DataTable config
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaCompromissosCancelados',
    title: t('Compromissos Cancelados'),
    filename: `compromissos_cancelados_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pagination: true,
    pageLength: 25,
    heightMode: 'fill-viewport',
    rowSelection: 'single'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'compromisso_id': 'text',
      'inicio': 'daterange',
      'status': 'select',
      'operador': 'select',
      'vendedor': 'select',
      'franquia': 'select',
      'nome_cliente': 'text',
      'location': 'text',
      'total': 'range',
      'cliente_telefone': 'text',
      'origem': 'select'
    }
  };
});

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: 'CSV', loading: false }));

const statusLabelRenderer = (params: CellParams): Node => {
  const row = (params.data || {}) as { status?: string };
  let severity = '';
  let icone = '';

  switch (row.status) {
    case 'Pendente':
      severity = 'warn';
      icone = 'exclamation-triangle';
      break;
    case 'Respondido':
      severity = 'info';
      icone = 'clock';
      break;
    case 'Aprovado':
      severity = 'success';
      icone = 'check';
      break;
    case 'Parcialmente concluído':
      severity = 'info';
      icone = 'check';
      break;
    case 'Todos os serviços realizados':
      severity = 'primary';
      icone = 'flag';
      break;
    case 'Cancelado':
      severity = 'secondary';
      icone = 'times';
      break;
    default:
      severity = 'secondary';
      icone = 'question';
  }

  const html = `<span class='status-badge status-${severity}'><i class='pi pi-${icone}'></i> ${params.value || ''}</span>`;
  const container = document.createElement('span');
  container.innerHTML = html;
  return container.firstChild as Node;
};

const tableColumns = computed(() => {
  return [
    {
      data: 'compromisso_id',
      title: 'ID',
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (value: Id | undefined) => {
        if (value) eventBus.emit('compromisso.editRequested', value);
      },
      dataType: 'number',
    },
    {
      data: 'status',
      title: t('Status', 'Status'),
      width: 200,
      resizable: true,
      sortable: true,
      cellRenderer: statusLabelRenderer,
    },
    {
      data: 'operador',
      title: t('Operador', 'Operador'),
      width: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'vendedor',
      title: t('Vendedor', 'Vendedor'),
      width: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'compromisso_data_hora_inicio',
      title: t('Data', 'Data'),
      width: 180,
      resizable: true,
      sortable: true,
      renderType: 'datetime',
      dataType: 'date',
    },
    {
      data: 'franquia',
      title: t('Franquia', 'Franquia'),
      width: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'nome_cliente',
      title: t('Cliente', 'Cliente'),
      width: 220,
      resizable: true,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (_value: unknown, row: { cliente_id?: Id } | undefined) => {
        if (row?.cliente_id) {
          eventBus.emit('cliente.edit', row.cliente_id);
        }
      },
    },
    {
      data: 'location',
      title: t('Local', 'Local'),
      width: 180,
      resizable: true,
      sortable: true,
    },
    {
      data: 'total',
      title: t('Total', 'Total'),
      width: 120,
      resizable: true,
      sortable: true,
      renderType: 'currency',
      dataType: 'number',
    },
  ];
});

const loadData = (): void => {
  log('GestaoClientesTabCompromissosCancelados', 'loadData', 'Triggering grid refresh');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('GestaoClientesTabCompromissosCancelados', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  emit('update:total', data.length || 0);
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('GestaoClientesTabCompromissosCancelados', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : err.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar compromissos cancelados: ') + message);
};

const handleGridEditClick = (compromissoId: Id): void => {
  log('GestaoClientesTabCompromissosCancelados', 'handleGridEditClick', 'Edit clicked from grid', compromissoId);
  handleEditarCompromisso(compromissoId);
};

const handleGridButtonClick = ({ value }: { value?: Id; data?: Record<string, unknown> }): void => {
  log('GestaoClientesTabCompromissosCancelados', 'handleGridButtonClick', 'Button clicked', { value });
};

const handleEditarCompromisso = (compromissoId: Id): void => {
  log('GestaoClientesTabCompromissosCancelados', 'handleEditarCompromisso', 'Opening edit compromisso modal', compromissoId);
  if (compromissoId) eventBus.emit('compromisso.editRequested', compromissoId);
};

const handleCompromissoSaved = (): void => {
  if (props.visible) loadData();
};

const handleClienteSaved = (): void => {
  if (props.visible) loadData();
};

onMounted(() => {
  log('GestaoClientesTabCompromissosCancelados', 'onMounted', 'Component mounted');

  eventBus.on('compromisso.saved', handleCompromissoSaved);
  eventBus.on('cliente.saved', handleClienteSaved);
});

onBeforeUnmount(() => {
  log('GestaoClientesTabCompromissosCancelados', 'onBeforeUnmount', 'Cleaning up listeners');
  eventBus.off('compromisso.saved', handleCompromissoSaved);
  eventBus.off('cliente.saved', handleClienteSaved);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.tab-pane-content {
  padding: 20px;
}
</style>

